import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import AffiliateCard from "@/components/affiliates/AffiliateCard";
import CommunityChat from "@/components/community/CommunityChat";
import { COMMUNITY_PROFILES, getCommunityProfile } from "@/data/communityProfiles";
import {
  MapPin,
  CalendarDays,
  ArrowRight,
  ArrowLeft,
  Users,
  BookOpen,
  Sparkles,
  Lightbulb,
  HandHeart,
  History,
  Building2,
  Target,
  Info,
} from "lucide-react";


import communityMeeting from "@/assets/community/community-meeting.jpg";
import localBusiness from "@/assets/community/local-business.jpg";
import mobileWaterfront from "@/assets/community/mobile-waterfront.jpg";
import volunteers from "@/assets/community/volunteers.jpg";

const collageImages = [
  { src: communityMeeting, alt: "Community members meeting in an Alabama community center" },
  { src: localBusiness, alt: "Small business corridor in an Alabama downtown" },
  { src: mobileWaterfront, alt: "Mobile, Alabama waterfront at sunset" },
  { src: volunteers, alt: "Volunteers organizing at a neighborhood event in Alabama" },
];

const formatEventDate = (iso?: string | null) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return {
      month: d.toLocaleDateString("en-US", { month: "short" }),
      day: d.toLocaleDateString("en-US", { day: "2-digit" }),
      weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
    };
  } catch {
    return null;
  }
};

const formatEventTime = (iso?: string | null) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return "";
  }
};

type PartnerEvent = {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  url: string | null;
  starts_at: string;
  ends_at: string | null;
  focus_area: string | null;
  kind: string | null;
  affiliates: { name: string; slug: string } | null;
};

type CaseStudyRow = {
  project_id: string;
  idea_id: string;
  idea_title: string;
  idea_description: string | null;
  idea_category: string | null;
  idea_image_url: string | null;
  author_display_name: string | null;
  latest_outcome: string;
  latest_summary: string;
  latest_lesson_at: string;
  public_lessons_count: number;
  tags: string[] | null;
};

type AffiliateRow = {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  location: string | null;
  category: string;
  services: string[];
  logo_url: string | null;
  featured: boolean;
  tier: string;
};

const SectionHeader = ({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  action?: { label: string; href: string };
}) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
    <div className="max-w-2xl">
      <span className="text-primary text-xs font-semibold tracking-[0.2em] uppercase">{eyebrow}</span>
      <h2 className="font-display text-2xl md:text-3xl font-bold mt-2 text-foreground">{title}</h2>
      {description && <p className="text-muted-foreground mt-2 max-w-xl">{description}</p>}
    </div>
    {action && (
      <Link
        to={action.href}
        className="inline-flex items-center gap-1 text-sm text-primary font-semibold hover:underline shrink-0"
      >
        {action.label} <ArrowRight className="h-4 w-4" />
      </Link>
    )}
  </div>
);

const EmptyState = ({
  icon: Icon,
  title,
  body,
  cta,
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  cta?: { label: string; href: string };
}) => (
  <Card className="p-8 md:p-10 bg-cream border-dashed border-border/60 text-center">
    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
      <Icon className="h-5 w-5 text-primary" />
    </div>
    <h3 className="font-display font-semibold text-lg mb-1">{title}</h3>
    <p className="text-sm text-muted-foreground max-w-md mx-auto mb-5">{body}</p>
    {cta && (
      <Button asChild variant="outline" size="sm">
        <Link to={cta.href}>{cta.label}</Link>
      </Button>
    )}
  </Card>
);

const CommunityDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const profile = slug ? getCommunityProfile(slug) : null;

  useEffect(() => {
    document.title = profile ? `${profile.name} | Alabama Communities | emrkt.co` : "Community | emrkt.co";
  }, [profile]);

  if (!profile) {
    return (
      <div className="min-h-screen bg-cream text-foreground">
        <SiteHeader />
        <main className="pt-32 pb-20">
          <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center">
            <h1 className="font-display text-2xl font-bold mb-2">Community profile not found</h1>
            <p className="text-muted-foreground mb-6">
              We don&apos;t have a profile for that place yet.
            </p>
            <Button asChild variant="outline">
              <Link to="/communities">Back to communities</Link>
            </Button>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream text-foreground">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section className="relative border-b border-border/40 overflow-hidden">
          {profile.hero_image_url && (
            <div className="absolute inset-0 z-0">
              <img
                src={profile.hero_image_url}
                alt={`${profile.name} hero`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-cream" />
            </div>
          )}
          <div className="relative z-10 container mx-auto px-4 sm:px-6 pt-28 pb-12 md:pt-36 md:pb-16 max-w-5xl">
            <Link
              to="/communities"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
            >
              <ArrowLeft className="h-4 w-4" /> Back to communities
            </Link>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">
              <MapPin className="h-3.5 w-3.5" />
              {profile.kind} profile
              {profile.parent && <span className="text-muted-foreground normal-case tracking-normal">· {profile.parent}</span>}
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] mb-4">
              {profile.name}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
              {profile.tagline}
            </p>
          </div>
        </section>

        {/* Overview & quick facts */}
        <section className="py-12 md:py-16 border-b border-border/40">
          <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">
                  <Info className="h-3.5 w-3.5" />
                  Overview
                </div>
                <p className="text-foreground/90 leading-relaxed text-lg">{profile.overview}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">
                  <Target className="h-3.5 w-3.5" />
                  Quick facts
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {profile.quick_facts.map((fact) => (
                    <Card key={fact.label} className="p-4 bg-background border-border">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                        {fact.label}
                      </div>
                      <div className="font-display font-semibold text-foreground">{fact.value}</div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* History & culture */}
        <section className="py-12 md:py-16 border-b border-border/40 bg-background/50">
          <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
            <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">
                  <History className="h-3.5 w-3.5" />
                  History
                </div>
                <p className="text-foreground/90 leading-relaxed">{profile.history}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">
                  <Building2 className="h-3.5 w-3.5" />
                  Culture
                </div>
                <p className="text-foreground/90 leading-relaxed">{profile.culture}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Aspirations */}
        <section className="py-12 md:py-16 border-b border-border/40">
          <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-6">
              <Lightbulb className="h-3.5 w-3.5" />
              Local aspirations
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {profile.aspirations.map((item, i) => (
                <Card key={i} className="p-5 bg-background border-border">
                  <div className="flex items-start gap-3">
                    <span className="font-display text-lg font-bold text-primary">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-foreground/90 leading-relaxed">{item}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 sm:px-6 max-w-4xl text-center">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              Work in or near {profile.name}?
            </h2>
            <p className="text-primary-foreground/80 text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
              Share an idea, find collaborators, and turn local knowledge into funded, visible action.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button asChild size="lg" variant="secondary">
                <Link to="/ideas">Share an idea</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                <Link to="/communities">Back to communities</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <CommunityChat
        communitySlug={profile.slug}
        communityName={profile.name}
        suggestions={[
          `What is happening in ${profile.name} right now?`,
          `Which Alabama bills matter for ${profile.name}?`,
          `Who is already working on projects here?`,
        ]}
      />
    </div>
  );
};

const Communities = () => {
  const { slug } = useParams<{ slug: string }>();
  if (slug) return <CommunityDetail />;
  // The community hub is now the site homepage.
  return <Navigate to="/" replace />;
};

export default Communities;

